import { Box, Modal } from "@mui/material";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { baseURL } from "../../../util/config";
import { setToast } from "../../../util/toast";
import Button from "../../extra/Button";
import Input from "../../extra/Input";
import { CLOSE_DIALOGUE } from "../../store/dialogue/dialogue.type";
import {
  createLanguage,
  updateLanguage,
} from "../../store/language/language.action";
import { permissionError } from "../../../util/Alert";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "95%",
  maxWidth: 600,
  bgcolor: "background.paper",
  border: "none",
  boxShadow: 24,
  p: 0,
  borderRadius: "12px",
  maxHeight: "95vh",
  overflowY: "auto",
};

const LanguageDialogue = () => {
  const dispatch = useDispatch();
  const { dialogueData } = useSelector((state) => state.dialogue);
  const hasPermission = useSelector((state) => state.admin.admin.flag);

  const [languageTitle, setLanguageTitle] = useState("");
  const [languageCode, setLanguageCode] = useState("");
  const [localLanguageTitle, setLocalLanguageTitle] = useState("");
  const [languageIcon, setLanguageIcon] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  console.log("imagePreview", imagePreview);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (dialogueData) {
      setLanguageTitle(dialogueData.languageTitle || "");
      setLanguageCode(dialogueData.languageCode || "");
      setLocalLanguageTitle(dialogueData.localLanguageTitle || "");
      const icon = dialogueData.languageIcon;
      if (icon) {
        setImagePreview(icon.startsWith("http") ? icon : `${baseURL}${icon}`);
      }
    }
  }, [dialogueData]);

  const handleClose = () => {
    dispatch({ type: CLOSE_DIALOGUE });
  };

  const validate = () => {
    let tempErrors = {};
    if (!languageTitle?.trim())
      tempErrors.languageTitle = "Language Title is required";
    if (!languageCode?.trim())
      tempErrors.languageCode = "Language Code is required";
    if (!localLanguageTitle?.trim())
      tempErrors.localLanguageTitle = "Localized Title is required";
    if (!imagePreview && !languageIcon)
      tempErrors.languageIcon = "Language Icon is required";
    setErrors(tempErrors);
    return Object.keys(tempErrors).length === 0;
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const allowedTypes = [
        "image/jpeg",
        "image/jpg",
        "image/png",
        "image/webp",
      ];
      if (!allowedTypes.includes(file.type)) {
        setErrors((prev) => ({
          ...prev,
          languageIcon: "Only JPG, PNG, JPEG, and WEBP formats are allowed",
        }));
        setLanguageIcon(null);
        setImagePreview(null);
        e.target.value = null;
        return;
      }
      setLanguageIcon(file);
      setImagePreview(URL.createObjectURL(file));
      setErrors((prev) => ({ ...prev, languageIcon: "" }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!hasPermission) return permissionError();
    if (!validate()) return;

    const formData = new FormData();
    formData.append("languageTitle", languageTitle.trim());
    formData.append("localLanguageTitle", localLanguageTitle.trim());
    if (languageIcon) {
      formData.append("languageIcon", languageIcon);
    }

    if (dialogueData) {
      // EDIT MODE: Payload Optimization (The API handles patches, but user said only modified fields)
      formData.append("languageCode", dialogueData.languageCode);

      // Check if anything changed
      let isChanged = false;
      if (languageTitle.trim() !== dialogueData.languageTitle) isChanged = true;
      if (localLanguageTitle.trim() !== dialogueData.localLanguageTitle)
        isChanged = true;
      if (languageIcon) isChanged = true;

      if (!isChanged) {
        setToast("info", "No changes made");
        handleClose();
        return;
      }

      dispatch(updateLanguage(formData));
      handleClose();
    } else {
      // ADD MODE
      formData.append("languageCode", languageCode.trim());
      formData.append("isActive", true);
      formData.append("isDefault", false);

      dispatch(createLanguage(formData));
      handleClose();
    }
  };

  return (
    <Modal open={true} onClose={handleClose}>
      <Box sx={style}>
        <div className="model-header p-3 border-bottom">
          <p className="m-0 fs-5 fw-bold">
            {dialogueData ? "Edit Language" : "Add Language"}
          </p>
        </div>
        <div className="model-body p-3">
          <form onSubmit={handleSubmit} className="row">
            <div className="col-12 mt-2">
              <Input
                label="Language Name"
                placeholder="Enter Language Name"
                value={languageTitle}
                errorMessage={errors.languageTitle}
                onChange={(e) => {
                  setLanguageTitle(e.target.value);
                  if (errors.languageTitle)
                    setErrors((prev) => ({ ...prev, languageTitle: "" }));
                }}
              />
            </div>
            <div className="col-12 mt-2">
              <Input
                label="Language Code"
                placeholder="Enter Language Code (e.g., en, hi)"
                value={languageCode}
                errorMessage={errors.languageCode}
                onChange={(e) => {
                  setLanguageCode(e.target.value);
                  if (errors.languageCode)
                    setErrors((prev) => ({ ...prev, languageCode: "" }));
                }}
                disabled={!!dialogueData}
              />
            </div>
            <div className="col-12 mt-2">
              <Input
                label="Localized Language Title"
                placeholder="Enter Localized Language Title"
                value={localLanguageTitle}
                errorMessage={errors.localLanguageTitle}
                onChange={(e) => {
                  setLocalLanguageTitle(e.target.value);
                  if (errors.localLanguageTitle)
                    setErrors((prev) => ({ ...prev, localLanguageTitle: "" }));
                }}
              />
            </div>
            <div className="col-12 mt-2">
              <Input
                label="Language Icon"
                type="file"
                accept=".jpg,.jpeg,.png,.webp"
                errorMessage={errors.languageIcon}
                onChange={handleImageChange}
              />
              <p className="m-0 mt-1 text-muted" style={{ fontSize: "12px" }}>
                Allowed formats: JPG, PNG, JPEG, WEBP
              </p>
              {imagePreview && (
                <div className="mt-2" style={{ textAlign: "start" }}>
                  <img
                    src={imagePreview}
                    alt="Preview"
                    style={{
                      width: "80px",
                      height: "80px",
                      objectFit: "cover",
                      borderRadius: "8px",
                      border: "1px solid #ddd",
                    }}
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = "/defaultImage.png";
                    }}
                  />
                </div>
              )}
            </div>
          </form>
        </div>
        <div className="model-footer p-3 border-top">
          <div className="d-flex justify-content-end gap-2">
            <Button
              btnName="Close"
              onClick={handleClose}
              btnColor="myCustomButton"
              style={{ borderRadius: "5px", width: "80px" }}
            />
            <Button
              btnName="Submit"
              onClick={handleSubmit}
              btnColor={`btnBlackPrime text-light ms-2`}
              style={{ borderRadius: "5px", width: "80px" }}
            />
          </div>
        </div>
      </Box>
    </Modal>
  );
};

export default LanguageDialogue;
